import { tableSortFilter } from '@/utils/arrayProcessing';
import {
  ClassifiedReferralRewards,
  Referral,
  ReferralFilterValue,
  ReferralProgressSummary,
  ReferralReward,
  ReferralRewardCoupon,
  ReferralRewardDisplay,
  ReferralRewardReference,
  ReferralSessionDisplay,
  ReferralUser,
} from '../types';

const DEFAULT_REQUIRED_COMPLETED_PAID_SESSIONS = 4;

export const REFERRALS_PERMISSION_MODULE = 'referrals';

export const REFERRALS_TABLE_HEAD = [
  { id: 'inviter', label: 'Inviter', align: 'left', minWidth: 180 },
  { id: 'invitee', label: 'Invitee', align: 'left', minWidth: 220 },
  { id: 'referral_status', label: 'Status', align: 'left', minWidth: 120 },
  { id: 'progress', label: 'Progress', align: 'left', minWidth: 160 },
  { id: 'remaining', label: 'Remaining', align: 'left', minWidth: 120 },
  { id: 'tracked_booking_id', label: 'Tracked booking', align: 'left' },
  { id: 'rewarded', label: 'Rewarded', align: 'left' },
  { id: 'created_at', label: 'Created date', align: 'left', minWidth: 140 },
];

export const REFERRAL_FILTER_LABELS: Record<ReferralFilterValue, string> = {
  all: 'All',
  pending: 'Pending',
  in_progress: 'In progress',
  ready_rewarded: 'Ready / Rewarded',
  expired: 'Expired',
};

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const toNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const toNullableNumberOrString = (value: unknown): number | string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) return value;
  return null;
};

const toStringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const toBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'rewarded'].includes(value.toLowerCase());
  }
  return false;
};

const toArray = <T>(value: T[] | T | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const firstDefined = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const formatKeyLabel = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toReferralUser = (value: unknown): ReferralUser | null => {
  const record = toRecord(value);

  if (!Object.keys(record).length) return null;

  const hasUserIdentity = Boolean(
    record.id ||
      record.email ||
      record.first_name ||
      record.firstName ||
      record.last_name ||
      record.lastName
  );

  return hasUserIdentity ? (record as ReferralUser) : null;
};

const getFirstReferralUser = (...values: unknown[]): ReferralUser | null => {
  for (const value of values) {
    const user = toReferralUser(value);
    if (user) return user;
  }

  return null;
};

export const getReferralStatus = (referral: Referral) =>
  toStringValue(firstDefined(referral.referral_status, referral.status)) ??
  'pending';

export const getReferralInviter = (referral: Referral): ReferralUser | null =>
  getFirstReferralUser(
    referral.inviter_user,
    referral.inviter,
    referral.referrer_user,
    referral.referrer,
    referral.referred_by_user,
    referral.referred_by,
    referral.invited_by_user,
    referral.invited_by,
    referral.user,
    referral.users,
    referral.client
  );

export const getReferralInviterId = (
  referral: Referral
): number | string | null =>
  toNullableNumberOrString(
    firstDefined(
      getReferralInviter(referral)?.id,
      referral.inviter_id,
      referral.inviter_user_id,
      referral.referrer_id,
      referral.referrer_user_id,
      referral.referred_by_id,
      referral.referred_by_user_id,
      referral.invited_by_id,
      referral.invited_by_user_id,
      typeof referral.referred_by === 'number' ||
        typeof referral.referred_by === 'string'
        ? referral.referred_by
        : null,
      typeof referral.invited_by === 'number' ||
        typeof referral.invited_by === 'string'
        ? referral.invited_by
        : null,
      referral.user_id
    )
  );

export const getReferralInviterDisplay = (referral: Referral) => {
  const inviter = getReferralInviter(referral);
  const displayName = getReferralUserDisplayName(inviter, '');

  if (displayName) return displayName;

  const inviterEmail = toStringValue(
    firstDefined(referral.inviter_email, referral.referrer_email)
  );
  if (inviterEmail) return inviterEmail;

  const inviterId = getReferralInviterId(referral);
  return inviterId ? `User #${inviterId}` : 'No inviter details returned';
};

export const getReferralInvitee = (referral: Referral): ReferralUser | null =>
  referral.invitee_user ?? referral.invitee ?? referral.referred_user ?? null;

export const getReferralUserDisplayName = (
  user?: ReferralUser | null,
  fallback = 'N/A'
) => {
  if (!user) return fallback;

  const name = [
    user.first_name ?? user.firstName,
    user.last_name ?? user.lastName,
  ]
    .filter(Boolean)
    .join(' ');
  return name || user.email || (user.id ? `User #${user.id}` : fallback);
};

export const getReferralInviteeDisplay = (referral: Referral) => {
  const invitee = getReferralInvitee(referral);
  return (
    getReferralUserDisplayName(invitee, '') ||
    referral.invitee_email ||
    'Invitee has not signed up'
  );
};

export const hasReferralInviteeUser = (referral: Referral) => {
  const invitee = getReferralInvitee(referral);
  return Boolean(invitee?.id || invitee?.email || invitee?.first_name);
};

export const getReferralProgress = (
  referral: Referral
): ReferralProgressSummary => {
  const progress = referral.reward_progress ?? {};
  const required = toNumber(
    progress.required_completed_paid_sessions,
    DEFAULT_REQUIRED_COMPLETED_PAID_SESSIONS
  );
  const rawCompleted = toNumber(progress.completed_paid_sessions, 0);
  const isRewarded = toBoolean(progress.is_rewarded);
  const completed = isRewarded ? required : Math.min(rawCompleted, required);
  const remaining = isRewarded
    ? 0
    : toNumber(
        progress.remaining_completed_paid_sessions,
        Math.max(required - completed, 0)
      );

  return {
    required,
    completed,
    remaining: Math.max(remaining, 0),
    trackedBookingId: toNullableNumberOrString(progress.tracked_booking_id),
    qualifiedBookingId: toNullableNumberOrString(progress.qualified_booking_id),
    qualifiedSessionId: toNullableNumberOrString(progress.qualified_session_id),
    isRewarded,
  };
};

export const isReferralExpired = (referral: Referral) => {
  const status = getReferralStatus(referral).toLowerCase();
  if (status === 'expired') return true;
  if (referral.expired_at) return true;

  if (referral.expires_at) {
    const expiresAt = new Date(referral.expires_at);
    return !Number.isNaN(expiresAt.getTime()) && expiresAt < new Date();
  }

  return false;
};

export const getReferralFilterValue = (
  referral: Referral
): ReferralFilterValue => {
  if (isReferralExpired(referral)) return 'expired';

  const progress = getReferralProgress(referral);

  if (progress.isRewarded || progress.completed >= progress.required) {
    return 'ready_rewarded';
  }

  if (progress.completed > 0 && progress.remaining > 0) {
    return 'in_progress';
  }

  return 'pending';
};

export const getReferralStatusLabelColor = (referral: Referral) => {
  const filterValue = getReferralFilterValue(referral);

  if (filterValue === 'ready_rewarded') return 'success';
  if (filterValue === 'in_progress') return 'info';
  if (filterValue === 'expired') return 'error';

  return 'warning';
};

export const getRewardedLabelColor = (referral: Referral) =>
  getReferralProgress(referral).isRewarded ? 'success' : 'default';

export const getReferralNeutralStates = (referral: Referral) => {
  const progress = getReferralProgress(referral);
  const states: string[] = [];

  if (!hasReferralInviteeUser(referral)) {
    states.push('Invitee has not signed up');
  }

  if (!progress.trackedBookingId) {
    states.push('No recurring booking yet');
  }

  if (progress.completed === 0) {
    states.push(`0 / ${progress.required} sessions complete`);
  }

  return states;
};

const getSearchableRewardValues = (referral: Referral) =>
  [
    ...toArray(referral.reward),
    ...toArray(referral.rewards),
    referral.inviter_coupon,
    referral.invitee_coupon,
    referral.inviter_voucher,
    referral.invitee_voucher,
  ]
    .flatMap((item) => {
      const record = toRecord(item);
      return [
        record.code,
        record.coupon_code,
        record.voucher_code,
        record.reference,
        record.reward_reference,
        record.idempotency_key,
      ];
    })
    .filter((value): value is string => typeof value === 'string');

const SESSION_COLLECTION_KEYS = [
  'completed_paid_session_details',
  'completed_paid_session_detail',
  'completed_paid_sessions_list',
  'completed_session_details',
  'counted_sessions',
  'paid_sessions',
  'qualifying_sessions',
  'sessions',
];

const SESSION_ID_KEYS = [
  'completed_paid_session_ids',
  'completed_session_ids',
  'counted_session_ids',
  'paid_session_ids',
  'session_ids',
];

const getNestedStatusValue = (value: unknown) => {
  const record = toRecord(value);
  return toStringValue(
    firstDefined(record.display, record.value, record.name, record.status)
  );
};

const normalizeSessionBreakdownItem = (
  item: unknown,
  fallbackBookingId: number | string | null = null,
  fallbackLabel: string | null = null
): ReferralSessionDisplay | null => {
  const primitiveId = toNullableNumberOrString(item);

  if (primitiveId) {
    return {
      id: primitiveId,
      bookingId: fallbackBookingId,
      date: null,
      time: null,
      status: null,
      paymentStatus: null,
      amountPaid: null,
      label: fallbackLabel,
    };
  }

  const record = toRecord(item);
  const id = toNullableNumberOrString(
    firstDefined(
      record.id,
      record.session_id,
      record.sessionId,
      record.booking_session_id,
      record.bookingSessionId
    )
  );

  if (!id) return null;

  return {
    id,
    bookingId: toNullableNumberOrString(
      firstDefined(record.booking_id, record.bookingId, fallbackBookingId)
    ),
    date: toStringValue(
      firstDefined(
        record.date,
        record.session_date,
        record.sessionDate,
        record.scheduled_at,
        record.scheduledAt,
        record.start_at,
        record.startAt,
        record.starts_at,
        record.startsAt
      )
    ),
    time: toStringValue(
      firstDefined(record.time, record.start_time, record.startTime)
    ),
    status:
      toStringValue(record.status) ??
      getNestedStatusValue(record.booking_session_status),
    paymentStatus:
      toStringValue(
        firstDefined(record.payment_status, record.paymentStatus)
      ) ?? getNestedStatusValue(record.booking_session_payment_status),
    amountPaid: toNullableNumberOrString(
      firstDefined(
        record.amount_paid,
        record.amountPaid,
        record.paid_amount,
        record.paidAmount,
        record.payment_amount,
        record.paymentAmount
      )
    ),
    label: toStringValue(firstDefined(record.label, record.description)),
  };
};

const collectSessionBreakdownItems = (
  source: unknown,
  fallbackBookingId: number | string | null
) => {
  const record = toRecord(source);
  const sessions: ReferralSessionDisplay[] = [];

  SESSION_COLLECTION_KEYS.forEach((key) => {
    const value = record[key];
    if (!Array.isArray(value)) return;

    value.forEach((item) => {
      const session = normalizeSessionBreakdownItem(item, fallbackBookingId);
      if (session) sessions.push(session);
    });
  });

  SESSION_ID_KEYS.forEach((key) => {
    const value = record[key];
    if (!Array.isArray(value)) return;

    value.forEach((id) => {
      const session = normalizeSessionBreakdownItem(id, fallbackBookingId);
      if (session) sessions.push(session);
    });
  });

  return sessions;
};

export const getReferralSessionBreakdown = (
  referral: Referral
): ReferralSessionDisplay[] => {
  const progress = getReferralProgress(referral);
  const sessions = [
    ...collectSessionBreakdownItems(
      referral.reward_progress,
      progress.trackedBookingId
    ),
    ...collectSessionBreakdownItems(referral, progress.trackedBookingId),
  ];

  if (!sessions.length && progress.qualifiedSessionId) {
    sessions.push({
      id: progress.qualifiedSessionId,
      bookingId: progress.qualifiedBookingId ?? progress.trackedBookingId,
      date: null,
      time: null,
      status: null,
      paymentStatus: null,
      amountPaid: null,
      label: 'Qualifying session',
    });
  }

  return sessions.reduce<ReferralSessionDisplay[]>((acc, session) => {
    const duplicate = acc.some(
      (existing) =>
        String(existing.id) === String(session.id) &&
        String(existing.bookingId ?? '') === String(session.bookingId ?? '')
    );

    if (!duplicate) acc.push(session);

    return acc;
  }, []);
};

export function applySortFilterReferrals({
  tableData,
  comparator,
  searchFieldValue,
  filterStatus,
}: {
  tableData: Referral[];
  comparator: (a: Referral, b: Referral) => number;
  searchFieldValue: string;
  filterStatus: ReferralFilterValue;
}) {
  let filtered = tableSortFilter(tableData, comparator);

  if (filterStatus !== 'all') {
    filtered = filtered.filter(
      (referral) => getReferralFilterValue(referral) === filterStatus
    );
  }

  if (searchFieldValue) {
    const query = searchFieldValue.toLowerCase();

    filtered = filtered.filter((referral) => {
      const progress = getReferralProgress(referral);
      const values = [
        referral.id,
        referral.invitee_email,
        getReferralStatus(referral),
        getReferralInviterDisplay(referral),
        getReferralInviteeDisplay(referral),
        progress.trackedBookingId,
        progress.qualifiedBookingId,
        progress.qualifiedSessionId,
        ...getReferralSessionBreakdown(referral).flatMap((session) => [
          session.id,
          session.bookingId,
          session.status,
          session.paymentStatus,
        ]),
        ...getSearchableRewardValues(referral),
      ];

      return values.some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(query)
      );
    });
  }

  return filtered;
}

export const getReferralFilterTabs = (referrals: Referral[]) => {
  const baseTabs: ReferralFilterValue[] = [
    'all',
    'pending',
    'in_progress',
    'ready_rewarded',
  ];

  if (referrals.some(isReferralExpired)) {
    baseTabs.push('expired');
  }

  return baseTabs.map((value) => ({
    value,
    label: REFERRAL_FILTER_LABELS[value],
    count:
      value === 'all'
        ? referrals.length
        : referrals.filter(
            (referral) => getReferralFilterValue(referral) === value
          ).length,
  }));
};

const createCouponDisplay = (
  source: unknown,
  sourceLabel?: string
): ReferralRewardDisplay | null => {
  const record = toRecord(source);
  const code = toStringValue(firstDefined(record.code, record.coupon_code));

  if (!code) return null;

  return {
    id: toNullableNumberOrString(record.id),
    code,
    description: toStringValue(record.description),
    value: toNullableNumberOrString(record.value),
    valueType: toStringValue(record.value_type),
    active:
      typeof record.active === 'boolean'
        ? record.active
        : record.active === undefined
        ? null
        : toBoolean(record.active),
    sourceLabel,
  };
};

const createCouponFromCode = (
  code: unknown,
  sourceLabel?: string
): ReferralRewardDisplay | null => {
  const couponCode = toStringValue(code);
  return couponCode ? { code: couponCode, sourceLabel } : null;
};

const getRewardRole = (
  reward: ReferralReward,
  inviterId?: number,
  inviteeId?: number
): 'inviter' | 'invitee' | null => {
  const record = toRecord(reward);
  const roleHint = [
    record.recipient,
    record.recipient_type,
    record.beneficiary,
    record.reward_type,
    record.type,
  ]
    .map((value) => String(value ?? '').toLowerCase())
    .join(' ');

  if (roleHint.includes('inviter') || roleHint.includes('referrer')) {
    return 'inviter';
  }

  if (roleHint.includes('invitee') || roleHint.includes('referred')) {
    return 'invitee';
  }

  const rewardUserId = toNumber(
    firstDefined(record.recipient_user_id, record.user_id),
    Number.NaN
  );

  if (Number.isFinite(rewardUserId)) {
    if (inviterId && rewardUserId === inviterId) return 'inviter';
    if (inviteeId && rewardUserId === inviteeId) return 'invitee';
  }

  return null;
};

const addCoupon = (
  target: ReferralRewardDisplay[],
  coupon: ReferralRewardDisplay | null
) => {
  if (!coupon) return;

  const duplicate = target.some(
    (existing) =>
      existing.code === coupon.code &&
      String(existing.id ?? '') === String(coupon.id ?? '')
  );

  if (!duplicate) {
    target.push(coupon);
  }
};

const REFERENCE_KEYS = [
  'id',
  'reference',
  'reward_reference',
  'external_reference',
  'idempotency_key',
  'idempotencyKey',
  'status',
  'type',
  'recipient',
  'recipient_type',
  'beneficiary',
  'created_at',
  'updated_at',
];

const addRewardReferences = (
  target: ReferralRewardReference[],
  reward: ReferralReward,
  prefix = 'Reward'
) => {
  const record = toRecord(reward);

  REFERENCE_KEYS.forEach((key) => {
    const value = record[key];
    if (value === undefined || value === null || value === '') return;

    const reference = {
      label: `${prefix} ${formatKeyLabel(key)}`,
      value: String(value),
    };

    if (
      !target.some(
        (existing) =>
          existing.label === reference.label &&
          existing.value === reference.value
      )
    ) {
      target.push(reference);
    }
  });
};

export const classifyReferralRewards = (
  referral: Referral
): ClassifiedReferralRewards => {
  const inviter: ReferralRewardDisplay[] = [];
  const invitee: ReferralRewardDisplay[] = [];
  const references: ReferralRewardReference[] = [];
  const inviterUser = getReferralInviter(referral);
  const inviteeUser = getReferralInvitee(referral);

  addCoupon(
    inviter,
    createCouponDisplay(referral.inviter_coupon, 'Inviter coupon')
  );
  addCoupon(
    inviter,
    createCouponDisplay(referral.inviter_voucher, 'Inviter voucher')
  );
  addCoupon(
    inviter,
    createCouponFromCode(
      firstDefined(referral.inviter_coupon_code, referral.inviter_voucher_code),
      'Inviter coupon'
    )
  );
  addCoupon(
    invitee,
    createCouponDisplay(referral.invitee_coupon, 'Invitee coupon')
  );
  addCoupon(
    invitee,
    createCouponDisplay(referral.invitee_voucher, 'Invitee voucher')
  );
  addCoupon(
    invitee,
    createCouponFromCode(
      firstDefined(referral.invitee_coupon_code, referral.invitee_voucher_code),
      'Invitee coupon'
    )
  );

  const rewardItems = [
    ...toArray(referral.reward),
    ...toArray(referral.rewards),
  ];

  rewardItems.forEach((reward, index) => {
    addCoupon(
      inviter,
      createCouponDisplay(reward.inviter_coupon, 'Inviter coupon')
    );
    addCoupon(
      inviter,
      createCouponDisplay(reward.inviter_voucher, 'Inviter voucher')
    );
    addCoupon(
      inviter,
      createCouponFromCode(
        firstDefined(reward.inviter_coupon_code, reward.inviter_voucher_code),
        'Inviter coupon'
      )
    );
    addCoupon(
      invitee,
      createCouponDisplay(reward.invitee_coupon, 'Invitee coupon')
    );
    addCoupon(
      invitee,
      createCouponDisplay(reward.invitee_voucher, 'Invitee voucher')
    );
    addCoupon(
      invitee,
      createCouponFromCode(
        firstDefined(reward.invitee_coupon_code, reward.invitee_voucher_code),
        'Invitee coupon'
      )
    );

    const role = getRewardRole(reward, inviterUser?.id, inviteeUser?.id);
    const genericCoupon =
      createCouponDisplay(reward.coupon, 'Reward coupon') ??
      createCouponDisplay(reward.voucher, 'Reward voucher') ??
      createCouponFromCode(
        firstDefined(reward.coupon_code, reward.voucher_code),
        'Reward coupon'
      );

    if (role === 'inviter') {
      addCoupon(inviter, genericCoupon);
    } else if (role === 'invitee') {
      addCoupon(invitee, genericCoupon);
    } else {
      addRewardReferences(references, reward, `Reward ${index + 1}`);
    }
  });

  return { inviter, invitee, references };
};
